import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import {
  PortcoGlobal,
  Surface,
  Header,
  HeaderInner,
  HeaderBrand,
  HeaderBrandMark,
  HeaderCrumb,
  Body,
  Hero,
  HeroEyebrow,
  HeroTitle,
  HeroSub,
  StatStrip,
  StatCell,
  StatLabel,
  StatValue,
  StatSub,
  Panel,
  PanelHead,
  PanelTitle,
  PanelMeta,
  PanelBody,
  Table,
  Thead,
  Th,
  Td,
  TdMono,
  Tr,
  Loading,
  Empty,
  portcoTokens,
  PortcoEmbeddedDark,
} from '../portco/PortcoTheme';

/**
 * Risk Center — landing (PGIM-tier).
 *
 * Tenant-wide cross-portfolio concentration roll-up. Six axes (issuer /
 * sector / industry / country / asset class / currency) plus a private-fund
 * manager view, anchored by the Herfindahl gauge and total AUM. Click any
 * portfolio in the Per-portfolio panel to drill to /risk/portfolios/:id
 * for the 4-method VaR detail (parametric / historical / MC / copula).
 *
 * Endpoints:
 *   GET /api/risk/concentration/tenant?topN=10
 *   GET /api/dimensions/portfolios
 */

interface ConcentrationRow {
  bucket: string;
  marketValue: number;
  pctOfAum: number;
}

interface PrivateFundRow {
  bucket: string;
  nav: number;
  pctOfNav: number;
}

interface Herfindahl {
  hhi?: number;
  interpretation?: string;
  issuerCount?: number;
  error?: string;
}

interface ConcentrationSummary {
  tenantId: number | null;
  totalAum: number;
  byIssuer: ConcentrationRow[];
  bySector: ConcentrationRow[];
  byIndustry: ConcentrationRow[];
  byCountry: ConcentrationRow[];
  byAssetClass: ConcentrationRow[];
  byCurrency: ConcentrationRow[];
  byPrivateFundManager: PrivateFundRow[];
  herfindahl: Herfindahl;
}

interface PortfolioLite {
  id: number;
  name: string;
  portfolioCode?: string;
  strategy?: string;
  totalNav?: number;
}

const fmtMoney = (v: number | null | undefined): string => {
  if (v == null || isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const fmtPct = (v: number | null | undefined, digits = 2): string => {
  if (v == null || isNaN(v)) return '—';
  return `${v.toFixed(digits)}%`;
};

const fmtInt = (v: number | null | undefined): string => {
  if (v == null || isNaN(v)) return '—';
  return v.toLocaleString();
};

const hhiTone = (hhi?: number): 'low' | 'mid' | 'high' => {
  if (hhi == null) return 'low';
  if (hhi < 1500) return 'low';
  if (hhi < 2500) return 'mid';
  return 'high';
};

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ChipBtn = styled.button<{ $active?: boolean }>`
  /* #86: token active state (inkPrimary bg / surfaceBody text) inverts to a
     white pill with invisible text under the dark embedded palette — use an
     explicit indigo-filled active + translucent inactive to match Analytics. */
  border: 1px solid
    ${(p) => (p.$active ? 'transparent' : 'rgba(255, 255, 255, 0.14)')};
  background: ${(p) => (p.$active ? '#6366f1' : 'rgba(255, 255, 255, 0.04)')};
  color: ${(p) => (p.$active ? '#ffffff' : portcoTokens.inkPrimary)};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
`;

const ConcentrationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
`;

const ConcentrationCard = styled.div`
  border: 1px solid ${portcoTokens.hairline};
  border-radius: 10px;
  padding: 14px 16px 12px;
  background: ${portcoTokens.surfaceTile};
`;

const CardEyebrow = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
  margin-bottom: 4px;
`;

const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${portcoTokens.inkPrimary};
  margin-bottom: 10px;
`;

const Bar = styled.div<{ $pct: number; $accent?: string }>`
  position: relative;
  height: 6px;
  border-radius: 3px;
  background: ${portcoTokens.hairline};
  overflow: hidden;
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${(p) => Math.max(0, Math.min(100, p.$pct))}%;
    background: ${(p) => p.$accent || portcoTokens.inkPrimary};
  }
`;

const BarRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  margin-bottom: 6px;
  & > strong {
    font-weight: 600;
    color: ${portcoTokens.inkPrimary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & > span {
    color: ${portcoTokens.inkSubtle};
    font-variant-numeric: tabular-nums;
  }
`;

const HHIGauge = styled.div<{ $tone: 'low' | 'mid' | 'high' }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${(p) =>
    p.$tone === 'low'
      ? 'rgba(16,185,129,0.08)'
      : p.$tone === 'mid'
      ? 'rgba(245,158,11,0.10)'
      : 'rgba(239,68,68,0.10)'};
  border: 1px solid
    ${(p) =>
      p.$tone === 'low'
        ? 'rgba(16,185,129,0.30)'
        : p.$tone === 'mid'
        ? 'rgba(245,158,11,0.35)'
        : 'rgba(239,68,68,0.40)'};
  & > .hhi-label {
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-weight: 600;
    color: ${portcoTokens.inkSubtle};
  }
  & > .hhi-value {
    font-size: 20px;
    font-weight: 700;
    color: ${portcoTokens.inkPrimary};
    font-variant-numeric: tabular-nums;
  }
  & > .hhi-interp {
    font-size: 11px;
    color: ${(p) =>
      p.$tone === 'low' ? '#047857' : p.$tone === 'mid' ? '#b45309' : '#b91c1c'};
    font-weight: 600;
  }
`;

const PortfolioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
`;

const PortfolioTile = styled.button`
  text-align: left;
  border: 1px solid ${portcoTokens.hairline};
  background: ${portcoTokens.surfaceTile};
  border-radius: 10px;
  padding: 14px 16px;
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.15s ease;
  &:hover {
    border-color: ${portcoTokens.inkPrimary};
    transform: translateY(-1px);
  }
`;

const PortfolioName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${portcoTokens.inkPrimary};
  margin-bottom: 4px;
`;

const PortfolioMeta = styled.div`
  font-size: 11px;
  color: ${portcoTokens.inkSubtle};
  margin-bottom: 8px;
  display: flex;
  gap: 8px;
  & > span {
    &::after {
      content: '·';
      margin-left: 8px;
    }
    &:last-child::after {
      content: '';
    }
  }
`;

const PortfolioCta = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${portcoTokens.accent};
`;

type AxisKey =
  | 'byIssuer'
  | 'bySector'
  | 'byIndustry'
  | 'byCountry'
  | 'byAssetClass'
  | 'byCurrency';

const AXES: Array<{ key: AxisKey; label: string; eyebrow: string; accent: string }> = [
  { key: 'byIssuer', label: 'Top issuers', eyebrow: 'Issuer', accent: '#6366f1' },
  { key: 'bySector', label: 'Top sectors', eyebrow: 'Sector', accent: '#0ea5e9' },
  { key: 'byIndustry', label: 'Top industries', eyebrow: 'Industry', accent: '#14b8a6' },
  { key: 'byCountry', label: 'Top geographies', eyebrow: 'Country', accent: '#f59e0b' },
  { key: 'byAssetClass', label: 'Asset class mix', eyebrow: 'Asset class', accent: '#a855f7' },
  { key: 'byCurrency', label: 'Currency mix', eyebrow: 'Currency', accent: '#ef4444' },
];

interface RiskCenterScreenProps {
  /**
   * When true, render only the body content (no PortcoGlobal / Surface / Header /
   * Hero shell). Used when this screen is embedded as a tab inside another
   * surface (e.g. Analytics → Cross-Asset Risk).
   */
  embedded?: boolean;
}

const RiskCenterScreen: React.FC<RiskCenterScreenProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<ConcentrationSummary | null>(null);
  const [portfolios, setPortfolios] = useState<PortfolioLite[]>([]);
  const [topN, setTopN] = useState<number>(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [c, p] = await Promise.all([
          axios.get<ConcentrationSummary>(`/api/risk/concentration/tenant?topN=${topN}`),
          axios.get<any>('/api/dimensions/portfolios'),
        ]);
        if (cancelled) return;
        setSummary(c.data);
        const list = Array.isArray(p.data) ? p.data : p.data?.content || [];
        setPortfolios(list);
      } catch (e: any) {
        if (!cancelled)
          setError(e?.response?.data?.message || e?.message || 'Failed to load Risk Center.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [topN]);

  const hhi = summary?.herfindahl?.hhi;
  const hhiToneVal = hhiTone(hhi);
  const topIssuer = summary?.byIssuer?.[0];
  const topSector = summary?.bySector?.[0];
  const topAssetClass = summary?.byAssetClass?.[0];

  const heroSubText = useMemo(() => {
    if (!summary) return '';
    return `Cross-portfolio concentration roll-up — every cell sourced from fact_positions joined to dim_security_master, dim_issuer, dim_asset_class. HHI scored against the standard 1500 / 2500 thresholds. Click a portfolio to drill into 4-method VaR detail.`;
  }, [summary]);

  const innerBody = (
    <>
      {!embedded && (
        <Hero>
          <HeroEyebrow>Cross-asset CRO console</HeroEyebrow>
          <HeroTitle>Risk Center</HeroTitle>
          <HeroSub>{heroSubText}</HeroSub>
        </Hero>
      )}
      {embedded && (
        <Hero style={{ paddingTop: 12 }}>
          <HeroEyebrow>Cross-asset CRO console</HeroEyebrow>
          <HeroTitle style={{ fontSize: 22 }}>Tenant concentration & VaR drilldown</HeroTitle>
          <HeroSub>{heroSubText}</HeroSub>
        </Hero>
      )}
    </>
  );

  if (embedded) {
    // #86: hosted as a tab inside the dark Analytics workspace — retint the
    // portco light palette to the institutional dark chrome via the sanctioned
    // .portco-embedded override (transparent surfaces let the shell show
    // through), instead of rendering the standalone white Surface.
    return (
      <div className="portco-embedded">
        <PortcoGlobal />
        <PortcoEmbeddedDark />
        <Surface className="portco-surface" style={{ minHeight: 0 }}>
          <Body>
            {innerBody}
            {loading && <Loading>Loading tenant concentration…</Loading>}
            {error && !loading && <Empty>{error}</Empty>}
            {!loading && !error && summary && renderSummaryBlocks()}
          </Body>
        </Surface>
      </div>
    );
  }

  function renderSummaryBlocks() {
    if (!summary) return null;
    return (
      <>
        <StatStrip>
          <StatCell>
            <StatLabel>Tenant AUM</StatLabel>
            <StatValue>{fmtMoney(summary.totalAum)}</StatValue>
            <StatSub>
              {fmtInt(summary.herfindahl?.issuerCount)} issuer
              {summary.herfindahl?.issuerCount === 1 ? '' : 's'} · {portfolios.length} portfolio
              {portfolios.length === 1 ? '' : 's'}
            </StatSub>
          </StatCell>
          <StatCell>
            <StatLabel>Herfindahl index</StatLabel>
            <StatValue>
              <HHIGauge $tone={hhiToneVal}>
                <span className="hhi-label">HHI · 0–10,000 scale</span>
                <span className="hhi-value">{hhi == null ? '—' : hhi.toFixed(0)}</span>
                <span className="hhi-interp">
                  {summary.herfindahl?.interpretation || '—'}
                </span>
              </HHIGauge>
            </StatValue>
            <StatSub>{'<1500 diversified · 1500–2500 moderate · >2500 high'}</StatSub>
          </StatCell>
          <StatCell>
            <StatLabel>Top issuer</StatLabel>
            <StatValue>{topIssuer?.bucket || '—'}</StatValue>
            <StatSub>
              {fmtPct(topIssuer?.pctOfAum)} of AUM · {fmtMoney(topIssuer?.marketValue)}
            </StatSub>
          </StatCell>
          <StatCell>
            <StatLabel>Top sector · asset class</StatLabel>
            <StatValue>
              {topSector?.bucket || '—'}{' '}
              <span style={{ color: portcoTokens.inkSubtle, fontSize: 14 }}>·</span>{' '}
              {topAssetClass?.bucket || '—'}
            </StatValue>
            <StatSub>
              {fmtPct(topSector?.pctOfAum)} sector · {fmtPct(topAssetClass?.pctOfAum)} asset class
            </StatSub>
          </StatCell>
        </StatStrip>

        <Panel>
          <PanelHead>
            <PanelTitle>Concentration map</PanelTitle>
            <FilterRow>
              <ChipBtn $active={topN === 5} onClick={() => setTopN(5)}>Top 5</ChipBtn>
              <ChipBtn $active={topN === 10} onClick={() => setTopN(10)}>Top 10</ChipBtn>
              <ChipBtn $active={topN === 20} onClick={() => setTopN(20)}>Top 20</ChipBtn>
            </FilterRow>
          </PanelHead>
          <PanelBody>
            <ConcentrationGrid>
              {AXES.map((axis) => {
                const rows = (summary[axis.key] || []) as ConcentrationRow[];
                if (!rows.length) {
                  return (
                    <ConcentrationCard key={axis.key}>
                      <CardEyebrow>{axis.eyebrow}</CardEyebrow>
                      <CardTitle>{axis.label}</CardTitle>
                      <Empty>No data on this axis.</Empty>
                    </ConcentrationCard>
                  );
                }
                return (
                  <ConcentrationCard key={axis.key}>
                    <CardEyebrow>{axis.eyebrow}</CardEyebrow>
                    <CardTitle>{axis.label}</CardTitle>
                    {rows.map((r) => (
                      <div key={r.bucket} style={{ marginBottom: 8 }}>
                        <BarRow>
                          <strong title={r.bucket}>{r.bucket}</strong>
                          <span>{fmtPct(r.pctOfAum)} · {fmtMoney(r.marketValue)}</span>
                        </BarRow>
                        <Bar $pct={r.pctOfAum} $accent={axis.accent} />
                      </div>
                    ))}
                  </ConcentrationCard>
                );
              })}
            </ConcentrationGrid>
          </PanelBody>
        </Panel>

        {(summary.byPrivateFundManager?.length || 0) > 0 && (
          <Panel>
            <PanelHead>
              <PanelTitle>Private-fund manager concentration</PanelTitle>
              <PanelMeta>NAV-weighted across PE / RE / private credit funds</PanelMeta>
            </PanelHead>
            <Table>
              <Thead>
                <tr>
                  <Th>Manager</Th>
                  <Th>NAV</Th>
                  <Th>% of fund NAV</Th>
                </tr>
              </Thead>
              <tbody>
                {summary.byPrivateFundManager.map((row) => (
                  <Tr key={row.bucket}>
                    <Td>{row.bucket}</Td>
                    <TdMono>{fmtMoney(row.nav)}</TdMono>
                    <TdMono>{fmtPct(row.pctOfNav)}</TdMono>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Panel>
        )}

        <Panel>
          <PanelHead>
            <PanelTitle>Per-portfolio drilldown</PanelTitle>
            <PanelMeta>Click any portfolio for 4-method VaR + factor shock + copula</PanelMeta>
          </PanelHead>
          <PanelBody>
            {portfolios.length === 0 ? (
              <Empty>No portfolios visible to this tenant.</Empty>
            ) : (
              <PortfolioGrid>
                {portfolios.map((p) => (
                  <PortfolioTile
                    key={p.id}
                    onClick={() => navigate(`/risk/portfolios/${p.id}`)}
                  >
                    <PortfolioName>{p.name}</PortfolioName>
                    <PortfolioMeta>
                      {p.portfolioCode && <span>{p.portfolioCode}</span>}
                      {p.strategy && <span>{p.strategy}</span>}
                      {p.totalNav != null && <span>NAV {fmtMoney(p.totalNav)}</span>}
                    </PortfolioMeta>
                    <PortfolioCta>VaR drilldown →</PortfolioCta>
                  </PortfolioTile>
                ))}
              </PortfolioGrid>
            )}
          </PanelBody>
        </Panel>
      </>
    );
  }

  return (
    <>
      <PortcoGlobal />
      <Surface className="portco-surface">
        <Header>
          <HeaderInner>
            <HeaderBrand>
              <HeaderBrandMark>AF</HeaderBrandMark>
              <span>AxiomFlow / Risk Center</span>
            </HeaderBrand>
            <HeaderCrumb>
              <span>Risk</span>
              <span> / </span>
              <span>Concentration map</span>
            </HeaderCrumb>
          </HeaderInner>
        </Header>

        <Body>
          {innerBody}
          {loading && <Loading>Loading tenant concentration…</Loading>}
          {error && !loading && <Empty>{error}</Empty>}
          {!loading && !error && summary && renderSummaryBlocks()}
        </Body>
      </Surface>
    </>
  );
};

export default RiskCenterScreen;
